import { useState } from 'react';
import type { PlanStep } from '@verifyloop/core';
import { StepTypePill } from './Pills';

export interface StudioStepRecorderModalProps {
  onClose: () => void;
  onAppendStep: (step: PlanStep) => Promise<void>;
  currentStepCount: number;
}

export function StudioStepRecorderModal({
  onClose,
  onAppendStep,
  currentStepCount,
}: StudioStepRecorderModalProps): JSX.Element {
  const [type, setType] = useState<PlanStep['type']>('action');
  const [description, setDescription] = useState('');
  const [target, setTarget] = useState('');
  const [optional, setOptional] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleInsert = async () => {
    if (!description.trim()) return;
    setSubmitting(true);
    try {
      const step: PlanStep = {
        type,
        description: description.trim(),
        target: target.trim() || undefined,
        optional: optional || undefined,
      };
      await onAppendStep(step);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickPreset = (presetType: PlanStep['type'], presetDesc: string, presetTarget: string) => {
    setType(presetType);
    setDescription(presetDesc);
    setTarget(presetTarget);
  };

  return (
    <div className="modal-backdrop" onClick={onClose} style={{ zIndex: 100 }}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(580px, 95vw)',
          background: 'var(--bg-panel)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'var(--bg-panel)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>⏺️</span>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
                Studio Step Recorder
              </h3>
              <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>
                Interactively construct and append step #{currentStepCount + 1} to the flow canvas
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn btn--small btn--ghost"
            onClick={onClose}
            style={{ fontSize: 13, padding: '2px 8px' }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Quick Presets */}
          <div>
            <span style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 6 }}>
              Quick Action Presets
            </span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn--small btn--ghost"
                onClick={() => handleQuickPreset('navigation', 'Navigate to /login', '/login')}
                style={{ fontSize: 11, padding: '3px 8px' }}
              >
                🌐 Navigate /login
              </button>
              <button
                type="button"
                className="btn btn--small btn--ghost"
                onClick={() => handleQuickPreset('action', 'Fill email with {{user.email}}', 'input[name="email"]')}
                style={{ fontSize: 11, padding: '3px 8px' }}
              >
                ✏️ Fill &#123;&#123;user.email&#125;&#125;
              </button>
              <button
                type="button"
                className="btn btn--small btn--ghost"
                onClick={() => handleQuickPreset('action', 'Click the Sign in button', 'button[type="submit"]')}
                style={{ fontSize: 11, padding: '3px 8px' }}
              >
                👆 Click Submit
              </button>
              <button
                type="button"
                className="btn btn--small btn--ghost"
                onClick={() => handleQuickPreset('assertion', 'Verify dashboard heading is visible', 'h1')}
                style={{ fontSize: 11, padding: '3px 8px' }}
              >
                🎯 Assert Heading
              </button>
            </div>
          </div>

          {/* Step Type */}
          <div>
            <label style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Step Primitive
            </label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as any)}
                style={{
                  flex: 1,
                  padding: '7px 10px',
                  background: 'var(--bg-inset)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  color: 'var(--text)',
                  fontSize: 13,
                }}
              >
                <option value="navigation">🌐 navigation (Navigate to URL or path)</option>
                <option value="action">👆 action (Click, fill, submit element)</option>
                <option value="assertion">🎯 assertion (Verify text or element exists)</option>
                <option value="wait">⏳ wait (Delay or wait for selector)</option>
                <option value="extract">📥 extract (Capture value into variable)</option>
              </select>
              <StepTypePill type={type} />
            </div>
          </div>

          {/* Description */}
          <div>
            <label style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Intent Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Click the Checkout button or Fill email with {{user.email}}"
              style={{
                width: '100%',
                padding: '8px 10px',
                background: 'var(--bg-inset)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--text)',
                fontSize: 13,
              }}
            />
            <span style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2, display: 'block' }}>
              Supports template tokens: <code>&#123;&#123;user.email&#125;&#125;</code>, <code>&#123;&#123;user.password&#125;&#125;</code>, <code>&#123;&#123;timestamp&#125;&#125;</code>
            </span>
          </div>

          {/* Target Selector */}
          <div>
            <label style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Target Selector or URL
            </label>
            <input
              type="text"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="e.g. [data-testid='submit'] or button.primary or /checkout"
              style={{
                width: '100%',
                padding: '8px 10px',
                background: 'var(--bg-inset)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--text)',
                fontSize: 13,
                fontFamily: 'monospace',
              }}
            />
          </div>

          {/* Optional Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              id="optional-step-check"
              checked={optional}
              onChange={(e) => setOptional(e.target.checked)}
            />
            <label htmlFor="optional-step-check" style={{ fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>
              Optional step (if this step fails, do not fail the overall run verdict)
            </label>
          </div>
        </div>

        <div
          style={{
            padding: '14px 20px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            background: 'var(--bg-panel)',
          }}
        >
          <button type="button" className="btn btn--small btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--small btn--primary"
            disabled={submitting || !description.trim()}
            onClick={handleInsert}
            style={{ fontWeight: 600 }}
          >
            {submitting ? 'Appending…' : '➕ Append Step to Canvas'}
          </button>
        </div>
      </div>
    </div>
  );
}
